import { useState, useEffect } from "react";
import { useNotification } from "context/NotificationContext";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import {
  Accordion,
  AccordionActions,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Card,
  Grid,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import expedientesService from "../../../services/expedientesService";
import taskService from "../../../services/taskService";
import { DateFormat } from "../../../services/common";

const AddTask = (props) => {
  const { showNotification } = useNotification();
  const { onAddTask, taskToEdit, onCancelEdit } = props;
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [finishDate, setFinishDate] = useState("");
  const [selectedItem, setSelectedItem] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        const data = await expedientesService.getAllExpedientesFiltered("");
        setFiles(Array.isArray(data) ? data : []);
      } catch {
        showNotification("Error al cargar los expedientes", "error");
      }
    }
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (taskToEdit && files.length > 0) {
      setSelectedItem(taskToEdit.expediente_id ?? "");
      setTitle(taskToEdit.title ?? "");
      setDescription(taskToEdit.description ?? "");
      setFinishDate(taskToEdit.finish_date ? DateFormat(taskToEdit.finish_date) : "");
      setExpanded(true);
    }
  }, [taskToEdit, files]);

  const addTask = async () => {
    setSubmitting(true);
    try {
      const data = await taskService.createTask({
        expediente_id: selectedItem,
        fh_finish: finishDate,
        title,
        description,
      });
      if (data === 200) {
        showNotification("Registro agregado correctamente", "success");
        setFinishDate("");
        setTitle("");
        setDescription("");
        setSelectedItem("");
        if (onAddTask) onAddTask();
      } else {
        showNotification(data, "error");
      }
    } catch {
      showNotification("Error al agregar la tarea", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const editTask = async () => {
    setSubmitting(true);
    try {
      const response = await taskService.updateTaskById({
        id: taskToEdit.id,
        expediente_id: selectedItem,
        fh_finish: finishDate,
        title,
        description,
      });
      if (response.success) {
        showNotification("Registro actualizado correctamente", "success");
        setFinishDate("");
        setTitle("");
        setDescription("");
        setSelectedItem("");
        if (onAddTask) onAddTask();
        onCancelEdit();
      } else {
        showNotification(response.message, "error");
      }
    } catch {
      showNotification("Error al editar la tarea", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const cancelTask = () => {
    setSelectedItem("");
    setTitle("");
    setDescription("");
    setFinishDate("");
    onCancelEdit();
  };

  // Ordenar y mapear los archivos
  const sortedFiles = [...files].sort((a, b) => a.caratula.localeCompare(b.caratula));

  const options = sortedFiles.map((x) => ({
    label: `${x.numero}/${x.anio}${x.cuaderno ? `/${x.cuaderno}` : ""} - ${x.caratula}`,
    value: x.id,
  }));

  return (
    <MDBox position="relative">
      <MDBox position="relative" mb={2}>
        <Card>
          <MDBox position="relative" p={1}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={12}>
                <Accordion
                  expanded={expanded}
                  onChange={() => setExpanded((prev) => !prev)}
                  style={{ boxShadow: "none" }}
                  sx={{
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.action.hover
                        : "transparent !important",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <MDTypography display="block" variant="button" fontWeight="regular">
                      Agregar nueva tarea
                    </MDTypography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MDBox mb={2}>
                      <MDInput
                        mb={3}
                        type="date"
                        label="Fecha de vencimiento"
                        InputLabelProps={{ shrink: true }}
                        value={finishDate || ""}
                        onChange={(event) => {
                          setFinishDate(event.target.value);
                        }}
                      />
                    </MDBox>
                    <Autocomplete
                      options={options}
                      getOptionLabel={(option) => option.label}
                      value={options.find((opt) => opt.value === selectedItem) || null}
                      onChange={(e, newValue) => {
                        setSelectedItem(newValue ? newValue.value : "");
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Expediente" variant="outlined" />
                      )}
                      fullWidth
                      size="small"
                    />

                    <MDBox my={2}>
                      <MDInput
                        fullWidth
                        type="text"
                        label="Titulo"
                        multiline
                        value={title}
                        onChange={(event) => {
                          setTitle(event.target.value);
                        }}
                      />
                    </MDBox>
                    <MDBox mb={2}>
                      <MDInput
                        fullWidth
                        type="text"
                        label="Descripción"
                        multiline
                        rows={3}
                        value={description}
                        onChange={(event) => {
                          setDescription(event.target.value);
                        }}
                      />
                    </MDBox>
                  </AccordionDetails>
                  <AccordionActions>
                    {taskToEdit ? (
                      <>
                        <MDButton variant="gradient" color="secondary" onClick={cancelTask} disabled={submitting}>
                          Cancelar
                        </MDButton>
                        <MDButton variant="gradient" color="success" onClick={editTask} disabled={submitting}>
                          Editar
                        </MDButton>
                      </>
                    ) : (
                      <MDButton variant="gradient" color="success" onClick={addTask} disabled={submitting}>
                        Agregar
                      </MDButton>
                    )}
                  </AccordionActions>
                </Accordion>
              </Grid>
            </Grid>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
};

AddTask.propTypes = {
  onAddTask: PropTypes.func,
  taskToEdit: PropTypes.object,
  onCancelEdit: PropTypes.func,
};

export default AddTask;
